import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { CAREER_CHART_DATA } from '../data';

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: '#1a1a1a', color: '#fafaf8', padding: '16px 20px',
      fontFamily: 'var(--font-mono)', fontSize: '11px', maxWidth: '260px',
      border: '1px solid rgba(232,197,71,0.2)',
    }}>
      <div style={{ color: '#e8c547', letterSpacing: '0.15em', marginBottom: '8px', textTransform: 'uppercase' }}>{d.year} · {d.label}</div>
      <div style={{ opacity: 0.7, lineHeight: 1.6, fontFamily: 'var(--font-body)', fontSize: '12px' }}>{d.event}</div>
    </div>
  );
};

const CustomDot = ({ cx, cy, payload, activeIndex, index }) => {
  const isActive = index === activeIndex;
  const isLast = index === CAREER_CHART_DATA.length - 1;
  return (
    <g>
      <circle cx={cx} cy={cy} r={isActive || isLast ? 6 : 4} fill={isLast ? '#e8c547' : isActive ? '#1a1a1a' : 'var(--bg)'} stroke={isLast ? '#e8c547' : '#1a1a1a'} strokeWidth={isActive ? 2 : 1.5} />
      {isLast && <circle cx={cx} cy={cy} r={10} fill="none" stroke="#e8c547" strokeWidth={1} opacity={0.4} />}
    </g>
  );
};

export default function CareerChart() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [activeData, setActiveData] = useState(null);

  return (
    <section id="career" style={{ padding: '120px 0', background: 'var(--bg)', borderTop: '1px solid var(--section-border, rgba(26,26,26,0.06))' }}>
      <div className="fade-in" style={{ padding: '0 12%' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.28em', color: 'var(--ink-mid)', textTransform: 'uppercase', marginBottom: '16px' }}>
          Career Trajectory
        </div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 600, color: 'var(--ink)', lineHeight: 1, letterSpacing: '-0.03em', marginBottom: '12px' }}>
          The Chart
        </h2>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--ink-light)', fontWeight: 300, marginBottom: '56px' }}>
          8 years of career progression — hover any data point to expand the story behind it.
        </p>

        <div style={{
          height: '64px', marginBottom: '32px',
          display: 'flex', alignItems: 'center', gap: '16px',
          borderLeft: '2px solid #e8c547', paddingLeft: '20px',
          opacity: activeData ? 1 : 0, transition: 'opacity 0.3s',
        }}>
          {activeData && (<>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.2em', color: '#e8c547', textTransform: 'uppercase' }}>{activeData.year}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontStyle: 'italic', color: 'var(--ink)' }}>{activeData.label}</div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--ink-mid)', fontWeight: 300, maxWidth: '480px' }}>{activeData.event}</div>
          </>)}
        </div>

        <div style={{ height: 'clamp(200px, 40vh, 320px)', position: 'relative' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={CAREER_CHART_DATA}
              margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              onMouseMove={e => {
                if (e.activeTooltipIndex !== undefined) {
                  setActiveIndex(e.activeTooltipIndex);
                  setActiveData(CAREER_CHART_DATA[e.activeTooltipIndex]);
                }
              }}
              onMouseLeave={() => { setActiveIndex(null); setActiveData(null); }}
            >
              <defs>
                <linearGradient id="careerGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1a1a1a" stopOpacity={0.08} />
                  <stop offset="95%" stopColor="#1a1a1a" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="year" tick={{ fontFamily: 'var(--font-mono)', fontSize: 10, fill: '#999990', letterSpacing: '0.1em' }} axisLine={{ stroke: 'rgba(26,26,26,0.1)' }} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={50} stroke="rgba(26,26,26,0.05)" strokeDasharray="4 4" />
              <Area
                type="monotone" dataKey="value"
                stroke="#1a1a1a" strokeWidth={1.5}
                fill="url(#careerGrad)"
                dot={<CustomDot activeIndex={activeIndex} />}
                activeDot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', padding: '0 30px' }}>
          {CAREER_CHART_DATA.map((d, i) => (
            <div key={i} style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: 'var(--ink-light)', letterSpacing: '0.1em', textAlign: 'center', maxWidth: '60px', lineHeight: 1.4 }}>
              {d.label.split(' ').slice(0, 2).join('\n')}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
